'use client'

import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  OfferConditionTypeEnumV2,
  OfferDiscountTypeEnumV2,
  type SellerPromotionV2,
} from '@redteclab/api/clients/promotion-service'

import { useGlobalConfigContext } from '../../../global-config'
import {
  getFormattedPromotionDiscountedAmountOrPercentage,
  getFormattedPromotionDiscountValueWithCurrency,
} from '../../models'

type ProductSellerPromotionFlagProps = {
  sellerPromotion: SellerPromotionV2
}

export const ProductSellerPromotionFlag: FC<
  ProductSellerPromotionFlagProps
> = ({ sellerPromotion }) => {
  const intl = useIntl()
  const { superScripts } = useGlobalConfigContext()

  const legalFootnoteNumber = superScripts.sellerPromotions ? (
    <sup key="sellerPromotions">{superScripts.sellerPromotions}</sup>
  ) : null

  if (
    sellerPromotion.conditionType ===
      OfferConditionTypeEnumV2.MinimumSameItemQuantity &&
    sellerPromotion.discountType === OfferDiscountTypeEnumV2.FreeItems &&
    sellerPromotion.conditionValue > 0
  ) {
    return (
      <FormattedMessage
        id="seller.promotion.product.sameItem.freeItems"
        values={{
          payableItemCount:
            sellerPromotion.conditionValue - sellerPromotion.discountValue,
          sup: legalFootnoteNumber,
          targetItemCount: sellerPromotion.conditionValue,
        }}
      />
    )
  }

  if (
    sellerPromotion.conditionType ===
      OfferConditionTypeEnumV2.MinimumQuantity &&
    sellerPromotion.discountType === OfferDiscountTypeEnumV2.FreeItems &&
    sellerPromotion.conditionValue > 0
  ) {
    return (
      <FormattedMessage
        id="seller.promotion.product.freeItems"
        values={{
          freeItemCount: sellerPromotion.discountValue,
          minimumItemCount: sellerPromotion.conditionValue,
          sup: legalFootnoteNumber,
        }}
      />
    )
  }

  if (
    sellerPromotion.conditionType ===
      OfferConditionTypeEnumV2.MinimumOrderValue &&
    sellerPromotion.conditionValue > 0
  ) {
    return (
      <FormattedMessage
        id="seller.promotion.product.discount.mov"
        values={{
          discount: getFormattedPromotionDiscountedAmountOrPercentage(
            sellerPromotion,
            intl,
          ),
          minimumOrderValue: getFormattedPromotionDiscountValueWithCurrency(
            sellerPromotion.conditionValue,
            intl,
          ),
          sup: legalFootnoteNumber,
        }}
      />
    )
  }

  if (
    (sellerPromotion.conditionType ===
      OfferConditionTypeEnumV2.MinimumQuantity ||
      sellerPromotion.conditionType ===
        OfferConditionTypeEnumV2.MinimumSameItemQuantity) &&
    sellerPromotion.conditionValue > 0
  ) {
    return (
      <FormattedMessage
        id="seller.promotion.product.discount.minQuantity"
        values={{
          discount: getFormattedPromotionDiscountedAmountOrPercentage(
            sellerPromotion,
            intl,
          ),
          minimumItemCount: sellerPromotion.conditionValue,
          sup: legalFootnoteNumber,
        }}
      />
    )
  }

  return (
    <FormattedMessage
      id="seller.promotion.product.badge.discount"
      values={{
        sup: legalFootnoteNumber,
      }}
    />
  )
}
